import React, { useEffect, useMemo, useState } from 'react'
import { Stack, Grid, Box, Divider, Typography, InputAdornment } from '@mui/material'
import { IconButton } from '@mui/material_modern'
import { AddCircle as AddCircleIcon } from '@mui/icons-material_modern'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import { CURRENCY_OPTIONS, ALL_PAYMENT_METHOD_OPTIONS } from 'utils/constants'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import NewPaymentTermModal from 'components/generic/modals/dialog-modal/NewPaymentTermModal'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'
import { useGetSupplierTypesQuery } from 'services/customers'
import { useFormContext } from 'react-hook-form'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'

const FinancialInformationForm: React.FC = () => {
  const { watch, control, setValue, getValues, errors } = useFormContext()
  const customerId = useAppSelector(state => state.ui.formModalProps.customerId)
  const isUpdate = !!customerId

  const supplierTypeId = getValues('supplierTypeId')
  const { hasAtm, details } = useAppSelector(state => state.settings)
  const { data: supplierTypes } = useGetSupplierTypesQuery({})

  const [openPaymentTermModal, setOpenPaymentTermModal] = useState(false)
  const { paymentTermsOptions } = usePaymentTerms({ getOnlyActive: true })

  // Set default tax value
  useEffect(() => {
    if (!supplierTypes || !supplierTypeId || isUpdate) return
    const supplierType = supplierTypes.Items.find(supplierType => supplierType.id === supplierTypeId)
    setValue('taxRate', supplierType?.taxRate || 0, { shouldDirty: true })
  }, [isUpdate, supplierTypes, supplierTypeId, setValue])

  const yardHasGsPayConfigured = details.gsPayEnabled

  const watchedContacts = watch('contacts')

  const contactOptions = useMemo(() => {
    const contacts = watchedContacts || []
    return contacts.map(contact => {
      const missingFields: string[] = []
      if (!contact.phone1) missingFields.push('phone')
      if (!contact.email) missingFields.push('email')
      if (!contact.address1 && !contact.locationId) missingFields.push('address')

      return {
        id: contact.id,
        name: `${contact.firstName || ''} ${contact.lastName || ''}`,
        description:
          contact.phone1 && contact.email && (contact.address1 || contact.locationId)
            ? contact.phone1.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
            : `Missing: ${missingFields.join(', ')}`,
        disabled: missingFields.length > 0,
      }
    })
  }, [watchedContacts])

  const PAYMENT_METHOD_OPTIONS = useMemo(
    () =>
      ALL_PAYMENT_METHOD_OPTIONS?.filter(
        method => (hasAtm ? true : method.id !== 'atm') && (yardHasGsPayConfigured ? true : method.id !== 'gs_pay'),
      ),
    [hasAtm, yardHasGsPayConfigured],
  )

  return (
    <>
      <NewPaymentTermModal
        open={openPaymentTermModal}
        onSave={newItemId => {
          setValue('defaultPaymentTermId', newItemId, { shouldDirty: true })
          setOpenPaymentTermModal(false)
        }}
        onClose={() => setOpenPaymentTermModal(false)}
      />
      <Stack flexGrow={1} sx={{ pl: 3, pr: 4 }}>
        <Box>
          <Typography variant="h6" color="textSecondary">
            Financial Information
          </Typography>
          <Box py={1}>
            <Divider />
          </Box>
          <Grid container pt={3} spacing={3} direction="column">
            {yardHasGsPayConfigured && (
              <Grid item md={6} xs={6}>
                <Select
                  name="gsPayContactId"
                  label="GreenSpark Pay Contact"
                  size="small"
                  defaultValue=""
                  control={control}
                  items={contactOptions}
                  error={!!errors?.gsPayContactId}
                  helperText={'This contact will be contacted for GreenSpark Pay onboarding and payment processing.'}
                />
              </Grid>
            )}
            <Grid item md={6} xs={6}>
              <Select
                name="preferredPaymentMethod"
                label="Preferred Payment Method"
                defaultValue=""
                control={control}
                items={PAYMENT_METHOD_OPTIONS}
                error={!!errors?.preferredPaymentMethod}
                clearable
              />
            </Grid>
            <Grid item md={6} xs={6}>
              <Grid container direction="row">
                <Grid item md={11} xs={11}>
                  <Select
                    name="defaultPaymentTermId"
                    label="Payment Terms"
                    defaultValue=""
                    control={control}
                    items={paymentTermsOptions}
                    error={!!errors?.defaultPaymentTermId}
                  />
                </Grid>
                <Grid item md={1} xs={1} alignSelf="center">
                  <IconButton id="add-payment-term-button" onClick={() => setOpenPaymentTermModal(true)}>
                    <AddCircleIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
            <Grid item md={6} xs={6}>
              <Input
                name="creditBalance"
                label="Credit Balance"
                defaultValue=""
                control={control}
                error={!!errors?.creditBalance}
                helperText={errors?.creditBalance?.message}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputComponent: InputFormat,
                  inputProps: {
                    fixedDecimalScale: true,
                    decimalScale: 0,
                  },
                }}
              />
            </Grid>
            <Grid item md={6} xs={6}>
              <Select
                name="defaultCurrency"
                label="Default Currency"
                defaultValue=""
                control={control}
                items={CURRENCY_OPTIONS}
                error={!!errors?.defaultCurrency}
              />
            </Grid>
            <Grid item md={6} xs={6}>
              <Input
                name="taxNumber"
                label="Tax Number"
                defaultValue=""
                control={control}
                error={!!errors?.taxNumber}
                helperText={errors?.taxNumber?.message}
                onKeyDown={event => {
                  if (!/^[a-zA-Z0-9-]+$/.test(event.key)) {
                    event.preventDefault()
                  }
                }}
              />
            </Grid>
            <Grid item md={6} xs={6}>
              <Input
                name="taxRate"
                label="Tax Rate"
                defaultValue=""
                control={control}
                error={!!errors?.taxRate}
                helperText={errors?.taxRate}
                InputProps={{
                  inputComponent: InputFormat,
                  inputProps: {
                    inputType: 'percent',
                    decimalScale: 3,
                  },
                }}
              />
            </Grid>
            <Grid item md={6} xs={6}>
              <Input
                name="accountingNumber"
                label="Accounting Number"
                defaultValue=""
                control={control}
                error={!!errors?.accountingNumber}
                helperText={errors?.accountingNumber?.message}
              />
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </>
  )
}

export default FinancialInformationForm
